// src/components/ProtectedRoute.js
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizeStoreRole(v) {
  const r = safeString(v).trim().toLowerCase();
  if (r === "tech") return "technician";
  if (r === "service_manager") return "manager";
  if (r === "store_manager") return "manager";
  return r;
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

export default function ProtectedRoute({
  allowedRoles,
  requirePlatform = false,
}) {
  const auth = useAuth();
  const loc = useLocation();

  const hydrated = !!auth?.hydrated;
  const isAuthed =
    typeof auth?.isAuthenticated === "boolean"
      ? auth.isAuthenticated
      : !!safeString(auth?.token).trim();

  const pathname = safeString(loc?.pathname);
  const isPlatformPath = pathname.startsWith("/platform");

  const platformRole = normalizePlatformRole(
    auth?.platformRole || auth?.user?.platformRole || ""
  );
  const hasPlatformAccess = !!platformRole;

  const storeRole = normalizeStoreRole(
    auth?.membership?.role ||
      auth?.user?.role ||
      auth?.currentUser?.role ||
      ""
  );

  if (!hydrated) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f3f4f6",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          padding: 24,
        }}
      >
        <div
          style={{
            width: "min(520px, 92vw)",
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ fontWeight: 900, color: "#111827" }}>
            Restoring session…
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: "#6b7280",
              lineHeight: 1.4,
            }}
          >
            Please wait while Flowtrak restores your login.
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
            Path: <b style={{ color: "#111827" }}>{pathname}</b>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace state={{ from: pathname }} />;
  }

  if ((requirePlatform || isPlatformPath) && !hasPlatformAccess) {
    return <Navigate to="/unauthorized" replace state={{ from: pathname }} />;
  }

  if (!requirePlatform && Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const allowed = allowedRoles.map((r) => normalizeStoreRole(r));
    if (!allowed.includes(storeRole)) {
      return <Navigate to="/unauthorized" replace state={{ from: pathname }} />;
    }
  }

  return <Outlet />;
}